import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  BarChart3,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Search,
  Zap,
  LineChart,
  PieChart,
  Code2,
  Filter,
  Layers,
  MousePointerClick,
  Database,
  Activity,
} from "lucide-react";

export const Route = createFileRoute("/services_/analytics")({
  head: () => ({
    meta: [
      {
        title:
          "Marketing Analytics Services — KMWAdvertising · GA4, GTM & Attribution",
      },
      {
        name: "description",
        content:
          "Marketing Analytics Services That Transform Data into Actionable Business Insights. GA4 Implementation, GTM, Looker Studio & Attribution Modeling by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Marketing Analytics Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Measure What Matters and Make Data-Driven Marketing Decisions. Complete tracking, conversion setup, and cross-channel ROAS dashboards.",
      },
    ],
  }),
  component: MarketingAnalyticsServicePage,
});

export function MarketingAnalyticsServicePage() {
  const whyAnalyticsMatters = [
    {
      title: "Measure Campaign Performance",
      desc: "Track how every marketing channel contributes to traffic, engagement, qualified leads, and online revenue.",
      icon: BarChart3,
    },
    {
      title: "Understand Customer Behaviour",
      desc: "Analyze how visitors interact with your website, landing pages, and digital assets to eliminate friction points.",
      icon: Activity,
    },
    {
      title: "Maximize Marketing ROI",
      desc: "Identify channels delivering the highest customer lifetime value so ad budgets can be reallocated effectively.",
      icon: TrendingUp,
    },
    {
      title: "Optimize Conversion Rates",
      desc: "Monitor user journeys and eliminate drop-off points that prevent visitors from completing desired conversions.",
      icon: Target,
    },
    {
      title: "Executive Business Decisions",
      desc: "Reliable, clean analytics provides leadership teams with confidence when making strategic growth investments.",
      icon: LineChart,
    },
  ];

  const ga4AndGtmServices = [
    {
      title: "GA4 Implementation & Configuration",
      desc: "Account setup, property configuration, event tracking, custom dimensions, enhanced eCommerce, and cross-domain tracking.",
      icon: BarChart3,
    },
    {
      title: "Google Tag Manager (GTM) Architecture",
      desc: "Tag/trigger/variable setup, custom JavaScript events, pixel installations, form tracking, and container debugging.",
      icon: Code2,
    },
    {
      title: "Conversion Tracking & Goal Setup",
      desc: "Accurate tracking for lead forms, calls, email clicks, eCommerce purchases, add-to-carts, bookings, and custom events.",
      icon: Target,
    },
    {
      title: "Looker Studio Dashboards",
      desc: "Custom real-time visual dashboards aggregating SEO, Google Ads, Meta Ads, SMM, and web traffic into single executive views.",
      icon: PieChart,
    },
    {
      title: "Multi-Touch Attribution Modelling",
      desc: "Evaluating how organic search, paid search, social, email, and display touchpoints contribute to final conversions.",
      icon: Layers,
    },
    {
      title: "Performance Optimisation & CRO Insights",
      desc: "Turning raw analytics data into actionable recommendations to improve UX, landing page conversion rates, and ROAS.",
      icon: Zap,
    },
  ];

  const trackedConversionEvents = [
    "Lead Form Submissions & Inquiries",
    "Tracked Phone Call Duration & Source",
    "Email & Contact Link Clicks",
    "eCommerce Purchase & Revenue Value",
    "Add-to-Cart & Checkout Steps",
    "Newsletter & PDF Whitepaper Downloads",
    "Appointment & Demo Bookings",
    "Video View Completion Milestones",
    "Custom Micro & Macro Business Goals",
  ];

  const analyticsToolsList = [
    "Google Analytics 4 (GA4)",
    "Google Tag Manager (GTM)",
    "Google Looker Studio",
    "Google Search Console",
    "Google Ads Conversion Tracking",
    "Meta Pixel & Conversions API (CAPI)",
    "Microsoft Clarity & Heatmaps",
    "HubSpot & Salesforce Analytics",
    "Custom SQL & BigQuery Export",
  ];

  const attributionTouchpoints = [
    "Organic Search (SEO)",
    "Paid Search (Google Ads)",
    "Paid Social (Meta, LinkedIn, X)",
    "Email Marketing & Automation",
    "Programmatic & Display Banners",
    "Referral & Affiliate Links",
    "Direct & Brand Searches",
    "Offline & Print QR Code Integration",
  ];

  const kmwaStrengths = [
    "25+ years of strategic advertising & media analytics leadership",
    "Certified GA4 & Google Tag Manager (GTM) specialists",
    "Advanced GTM & Conversions API (CAPI) technical deployment",
    "Custom Looker Studio real-time executive dashboards",
    "Cross-channel attribution modeling & budget reallocation",
    "Conversion Rate Optimisation (CRO) & UX audit consulting",
    "100% transparent reporting with zero data sampling bias",
    "AI-assisted predictive analytics & trend forecasting",
    "Full privacy compliance (GDPR, DPDP, First-Party Data)",
    "ROI-focused decision-making methodology",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">Marketing Analytics</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <BarChart3 className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Marketing Analytics Services That Transform Data into Actionable Business Insights
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                GA4, GTM, Looker Dashboards & Multi-Touch Attribution
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Measure What Matters and Make Data-Driven Marketing Decisions
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Every click, impression, website visit and customer interaction generates valuable data. The challenge isn't collecting information—it's turning that data into clear, actionable insights that improve campaign performance, user experience, and revenue.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Without accurate analytics, businesses struggle to understand which channels generate qualified leads and where ad budgets should be reallocated. Modern analytics delivers 100% visibility into customer journeys and ROI.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we provide comprehensive Marketing Analytics services helping businesses measure, analyze, and optimize every touchpoint across GA4, GTM, Looker Studio, and enterprise CRMs.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Analytics Audit Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY MARKETING ANALYTICS MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Measurement & Intelligence
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Marketing Analytics Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Successful digital marketing is built on precise measurement. Reliable analytics enables leadership to make confident, data-driven decisions.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whyAnalyticsMatters.map((item, idx) => {
                const AIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <AIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SERVICES SUITE & GA4/GTM IMPLEMENTATION */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Enterprise Data Architecture
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Marketing Analytics Services
              </h2>
            </div>

            {/* 6 SERVICES CARDS */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {ga4AndGtmServices.map((svc, idx) => {
                const SIcon = svc.icon;
                return (
                  <div key={idx} className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-4">
                    <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                      <SIcon className="h-5 w-5" />
                    </div>
                    <h4 className="font-display text-xl font-bold text-white">{svc.title}</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{svc.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* CONVERSIONS & ATTRIBUTION */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Conversion Events */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Target className="h-5 w-5 text-[var(--glow-purple)]" />
                  Tracked Conversion Events
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Measuring every critical customer action across your digital ecosystem:
                </p>
                <div className="space-y-2 pt-2">
                  {trackedConversionEvents.map((evt, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{evt}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Attribution Touchpoints */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Layers className="h-5 w-5 text-sky-400" />
                  Multi-Touch Attribution Touchpoints
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Understanding how various marketing channels collaborate to generate conversions:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {attributionTouchpoints.map((at, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                      <span>{at}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TOOLS & PLATFORMS */}
        <section className="py-16 md:py-24 border-b border-white/10 relative bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Analytics Ecosystem
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Tools & Platforms We Work With
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {analyticsToolsList.map((tool, i) => (
                <div key={i} className="p-3.5 rounded-xl glass border border-white/10 text-xs text-white/90 font-medium text-center">
                  {tool}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                25+ Years Strategic Analytics Heritage
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <BarChart3 className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              Marketing Analytics Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Make Data-Driven Marketing Decisions?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Contact our analytics specialists today for a comprehensive GA4 setup, GTM audit, and Looker Studio dashboard strategy.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Analytics Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
